import type { PerformanceReference } from '@felix/domain';
import { Button } from '@mantine/core';
import { IconFileCertificate, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { CP_STATUS_TONE } from './charter-party.js';
import { useNumber } from './numbers.js';
import { type ReferenceBasis, voyageReference } from './reference.js';
import type { VoyageSource } from './source.js';
import type { CharterPartyItem, CharterPartyRegisterData, Voyage } from './types.js';
import classes from './VoyageReference.module.css';

/**
 * Seferin CP'si ve performans referansı ekranda (ADR 0084 Ek 2): CP şartları salt okunur (veri CP
 * kaydında), CP yoksa "No charter party — owner's reference used". Add New Voyage, Voyage Details
 * ve analiz ekranları aynı parçaları kullanır.
 */

function useRefText() {
  const { t } = useTranslation();
  return (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.cpReference.${key}`, options);
}

/** Referansın kısa adı: "Charter party CP-…", "Owner's reference" ya da "No reference". */
export function useReferenceLabel() {
  const r = useRefText();
  return (reference: Pick<PerformanceReference, 'kind' | 'cpNo' | 'charterer'>) =>
    reference.kind === 'charter_party'
      ? r('label.charter_party', { no: reference.cpNo ?? reference.charterer ?? '' }).trim()
      : r(`label.${reference.kind}`);
}

/** CP şartları, salt okunur (kiracı, CP no, tür, dönem, hız, tüketim, hava sınırları). */
export function CpTermsFacts({ item }: { item: CharterPartyItem }) {
  const { t } = useTranslation();
  const r = useRefText();
  const n = useNumber();
  const terms = item.terms;
  const pair = (a: number | null, b: number | null, unit: string, digits: number) =>
    a === null && b === null ? '—' : `${n(a, digits)} / ${n.unit(b, unit, digits)}`;
  const weather = [
    terms.maxBeaufort !== null ? r('beaufort', { value: terms.maxBeaufort }) : null,
    terms.maxWaveHeightM !== null ? r('wave', { value: n(terms.maxWaveHeightM, 1) }) : null,
    terms.maxDouglas !== null ? r('douglas', { value: terms.maxDouglas }) : null,
    terms.maxSwellM !== null ? r('swell', { value: n(terms.maxSwellM, 1) }) : null,
  ].filter((v): v is string => v !== null);
  const period = item.period;
  return (
    <dl className={classes.facts}>
      <dt>{r('charterer')}</dt>
      <dd>{item.charterer ?? '—'}</dd>
      <dt>{r('cpNo')}</dt>
      <dd>{item.cpNo ?? '—'}</dd>
      <dt>{r('type')}</dt>
      <dd>{t(`voyage.charterParties.types.${item.cpType}`)}</dd>
      <dt>{r('period')}</dt>
      <dd>
        {period.start
          ? `${formatDateTime(period.start, 0)} → ${period.end ? formatDateTime(period.end, 0) : '…'}`
          : '—'}
      </dd>
      <dt>{r('status')}</dt>
      <dd>
        <StatusBadge tone={CP_STATUS_TONE[period.status]}>
          {t(`voyage.charterParties.status.${period.status}`)}
        </StatusBadge>
      </dd>
      <dt>{r('speed')}</dt>
      <dd>{pair(terms.speedLadenKn, terms.speedBallastKn, 'kn', 1)}</dd>
      <dt>{r('consumption')}</dt>
      <dd>{pair(terms.consumptionLadenMt, terms.consumptionBallastMt, 'MT/day', 1)}</dd>
      <dt>{r('weather')}</dt>
      <dd>{weather.length > 0 ? weather.join(' · ') : '—'}</dd>
    </dl>
  );
}

/** Referansın dört değeri (CP'siz ya da eski seferin kolonlarından CP). */
export function ReferenceValues({ reference }: { reference: PerformanceReference }) {
  const r = useRefText();
  const n = useNumber();
  const pair = (a: number | null, b: number | null, unit: string) =>
    a === null && b === null ? '—' : `${n(a, 1)} / ${n.unit(b, unit, 1)}`;
  return (
    <dl className={classes.facts}>
      {reference.kind === 'owner' && reference.ownerSource ? (
        <>
          <dt>{r('reference')}</dt>
          <dd>{r(`ownerSource.${reference.ownerSource}`)}</dd>
        </>
      ) : null}
      <dt>{r('speed')}</dt>
      <dd>{pair(reference.speedLadenKn, reference.speedBallastKn, 'kn')}</dd>
      <dt>{r('consumption')}</dt>
      <dd>{pair(reference.consumptionLadenMt, reference.consumptionBallastMt, 'MT/day')}</dd>
    </dl>
  );
}

/**
 * Referansın özeti: CP varsa şartları (salt okunur), CP yoksa sahibin referansı ve
 * "No charter party — owner's reference used", hiçbiri yoksa açık not. Sonuç etiketi Indicative.
 */
export function ReferenceSummary({
  reference,
  charterParty,
  basis,
}: {
  reference: PerformanceReference;
  charterParty: CharterPartyItem | null;
  basis: ReferenceBasis;
}) {
  const r = useRefText();
  return (
    <div className={classes.summary}>
      {charterParty ? (
        <>
          {basis ? <p className={classes.basis}>{r(`basis.${basis}`)}</p> : null}
          <CpTermsFacts item={charterParty} />
        </>
      ) : null}
      {!charterParty && reference.kind === 'charter_party' ? (
        <>
          {basis ? <p className={classes.basis}>{r(`basis.${basis}`)}</p> : null}
          <ReferenceValues reference={reference} />
        </>
      ) : null}
      {reference.kind === 'owner' ? (
        <>
          <p className={classes.notice} data-tone="info">
            <IconInfoCircle size={16} aria-hidden />
            <span>{r('noCp')}</span>
          </p>
          <ReferenceValues reference={reference} />
        </>
      ) : null}
      {reference.kind === 'none' && !charterParty ? (
        <p className={classes.notice} data-tone="warning">
          <IconInfoCircle size={16} aria-hidden />
          <span>{r('noReference')}</span>
        </p>
      ) : null}
      {reference.kind !== 'none' ? (
        <p className={classes.indicative}>
          <StatusBadge tone="info">{r('indicative')}</StatusBadge>
          <span>{r('indicativeNote')}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Voyage Details kartı: seferin CP'si (register'dan, salt okunur) ve referans. Referans sunucunun
 * hesabından (`performance.reference`); eski sunucuda register'la burada hesaplanır.
 */
export function VoyageReferencePanel({
  source,
  voyage,
  reference,
}: {
  source: VoyageSource;
  voyage: Voyage;
  reference: (PerformanceReference & { basis: ReferenceBasis }) | null | undefined;
}) {
  const r = useRefText();
  const shell = useShell();
  const [register, setRegister] = useState<CharterPartyRegisterData | null>(null);
  useEffect(() => {
    let live = true;
    if (!source.charterParties) return;
    source
      .charterParties(voyage.vesselId)
      .then((value) => {
        if (live) setRegister(value);
      })
      .catch(() => {
        if (live) setRegister(null);
      });
    return () => {
      live = false;
    };
  }, [source, voyage.vesselId]);
  const local = voyageReference(voyage, register);
  const view = reference ?? { ...local.reference, basis: local.basis };
  const charterParty =
    (view.charterPartyId && register?.items.find((i) => i.id === view.charterPartyId)) || null;
  const vesselQuery = source.scope === 'web' ? `?vessel=${voyage.vesselId}` : '';
  return (
    <Panel title={r('title')} icon={<IconFileCertificate size={20} stroke={1.8} />}>
      <ReferenceSummary reference={view} charterParty={charterParty} basis={view.basis} />
      {source.charterParties ? (
        <Button
          variant="subtle"
          size="compact-sm"
          px={0}
          mt="xs"
          onClick={() => shell?.navigate(`/voyage/charter-parties${vesselQuery}`)}
        >
          {r('openRegister')}
        </Button>
      ) : null}
    </Panel>
  );
}
